import { computed, h, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOperators } from 'dashboard/components-next/filter/operators';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useChannelIcon } from 'next/icon/provider';
import { createContactSearcher } from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import {
  buildAttributesFilterTypes,
  KANBAN_TASK_ATTRIBUTES,
} from 'dashboard/components-next/filter/helper/filterHelper';
import { KANBAN_PRIORITIES, KANBAN_TASK_STATUSES } from 'kanban/constants';

/**
 * Filtering context for Kanban cards.
 *
 * The attribute keys here must stay in sync with the backend whitelist in
 * fazer_ai/config/filters/kanban_task_filter_keys.yml -- an attribute missing
 * there is rejected with InvalidAttribute at request time.
 *
 * @param {import('vue').Ref<Object>|Object} board - the active funnel; its steps
 *   and assigned agents/inboxes drive the available options.
 * @returns {{ filterTypes: import('vue').ComputedRef<Array> }}
 */
export function useKanbanTaskFilterContext(board) {
  const { t } = useI18n();

  const taskAttributes = useMapGetter('attributes/getTaskAttributes');
  const labels = useMapGetter('labels/getLabels');
  const agents = useMapGetter('agents/getAgents');
  const inboxes = useMapGetter('inboxes/getInboxes');

  const {
    equalityOperators,
    presenceOperators,
    containmentOperators,
    comparisonOperators,
    dateOperators,
    getOperatorTypes,
  } = useOperators();

  const activeBoard = computed(() => unref(board) ?? {});

  // Steps belong to a funnel, so they are scoped to the active board. Agents and
  // inboxes fall back to the account-wide lists when the funnel has none
  // assigned, otherwise the option list would come up empty.
  //
  // steps_summary first: the board page reads its board from boards#index,
  // which renders with_steps: false, so `steps` only exists on the show payload.
  const stepOptions = computed(() =>
    (activeBoard.value.steps_summary ?? activeBoard.value.steps ?? []).map(
      step => ({
        id: step.id,
        name: step.name,
        icon: h('span', {
          class: 'rounded-full',
          style: { backgroundColor: step.color, height: '6px', width: '6px' },
        }),
      })
    )
  );

  const agentOptions = computed(() => {
    const assigned = activeBoard.value.assigned_agents ?? [];
    const source = assigned.length ? assigned : agents.value;
    return source.map(agent => ({ id: agent.id, name: agent.name }));
  });

  const inboxOptions = computed(() => {
    const assigned = activeBoard.value.assigned_inboxes ?? [];
    const source = assigned.length ? assigned : inboxes.value;
    // Only the fields the option needs: these objects live in the filter UI
    // state and get JSON-cloned on the way to a saved view, and a raw inbox
    // carries its channel config, website_token and hmac_token along.
    return source.map(inbox => ({
      id: inbox.id,
      name: inbox.name,
      icon: useChannelIcon(inbox).value,
    }));
  });

  const searchContacts = createContactSearcher();

  const searchContactOptions = async query => {
    const contacts = await searchContacts(query, {
      skipMinLength: true,
      reachableOnly: false,
    });

    // null means the request was aborted (a newer search is in-flight)
    if (contacts === null) return null;

    return contacts.map(contact => ({
      id: contact.id,
      name:
        contact.name ||
        contact.email ||
        contact.phoneNumber ||
        contact.identifier ||
        t('FILTER.CONTACT_FALLBACK', { id: contact.id }),
    }));
  };

  // presenceOperators carries more than presence for some entities; the
  // attributes below only accept these two.
  const presenceOnly = () =>
    presenceOperators.value.filter(op =>
      ['is_present', 'is_not_present'].includes(op.value)
    );

  const attribute = (key, i18nKey, overrides) => ({
    attributeKey: key,
    value: key,
    attributeName: t(`KANBAN.FILTERS.ATTRIBUTES.${i18nKey}`),
    label: t(`KANBAN.FILTERS.ATTRIBUTES.${i18nKey}`),
    dataType: 'text',
    attributeModel: 'standard',
    ...overrides,
  });

  const customFilterTypes = computed(() =>
    buildAttributesFilterTypes(
      taskAttributes.value,
      getOperatorTypes,
      'kanban_task'
    )
  );

  const filterTypes = computed(() => [
    attribute(KANBAN_TASK_ATTRIBUTES.STATUS, 'STATUS', {
      inputType: 'multiSelect',
      options: KANBAN_TASK_STATUSES.map(id => ({
        id,
        name: t(`KANBAN.STATUS.${id.toUpperCase()}`),
      })),
      filterOperators: equalityOperators.value,
    }),
    attribute(KANBAN_TASK_ATTRIBUTES.BOARD_STEP_ID, 'STEP', {
      inputType: 'multiSelect',
      options: stepOptions.value,
      dataType: 'number',
      filterOperators: equalityOperators.value,
    }),
    attribute(KANBAN_TASK_ATTRIBUTES.PRIORITY, 'PRIORITY', {
      inputType: 'multiSelect',
      // The null entry means "no priority", which is is_not_present here.
      options: KANBAN_PRIORITIES.filter(({ id }) => id).map(({ id }) => ({
        id,
        name: t(`KANBAN.PRIORITY.${id.toUpperCase()}`),
      })),
      filterOperators: presenceOperators.value,
    }),
    attribute(KANBAN_TASK_ATTRIBUTES.ASSIGNEE_ID, 'ASSIGNEE', {
      inputType: 'multiSelect',
      options: agentOptions.value,
      dataType: 'number',
      filterOperators: presenceOperators.value,
    }),
    attribute(KANBAN_TASK_ATTRIBUTES.PARTICIPANT_ID, 'PARTICIPANT', {
      inputType: 'multiSelect',
      options: agentOptions.value,
      dataType: 'number',
      filterOperators: presenceOperators.value,
    }),
    attribute(KANBAN_TASK_ATTRIBUTES.INBOX_ID, 'INBOX', {
      inputType: 'multiSelect',
      options: inboxOptions.value,
      dataType: 'number',
      filterOperators: presenceOperators.value,
    }),
    attribute(KANBAN_TASK_ATTRIBUTES.CONTACT_ID, 'CONTACT', {
      inputType: 'asyncSearchSelect',
      searchOptions: searchContactOptions,
      searchPlaceholder: t('FILTER.CONTACT_SEARCH_PLACEHOLDER'),
      dataType: 'number',
      filterOperators: presenceOperators.value,
    }),
    attribute(KANBAN_TASK_ATTRIBUTES.LABELS, 'LABELS', {
      inputType: 'multiSelect',
      options: labels.value.map(label => ({
        id: label.title,
        name: label.title,
        icon: h('span', {
          class: 'rounded-full',
          style: { backgroundColor: label.color, height: '6px', width: '6px' },
        }),
      })),
      filterOperators: presenceOperators.value,
    }),
    attribute(KANBAN_TASK_ATTRIBUTES.TITLE, 'TITLE', {
      inputType: 'plainText',
      filterOperators: containmentOperators.value,
    }),
    attribute(KANBAN_TASK_ATTRIBUTES.DESCRIPTION, 'DESCRIPTION', {
      inputType: 'plainText',
      filterOperators: [
        ...containmentOperators.value.filter(op =>
          ['contains', 'does_not_contain'].includes(op.value)
        ),
        ...presenceOnly(),
      ],
    }),
    attribute(KANBAN_TASK_ATTRIBUTES.VALUE, 'VALUE', {
      inputType: 'number',
      dataType: 'number',
      filterOperators: comparisonOperators.value,
    }),
    attribute(KANBAN_TASK_ATTRIBUTES.CREATED_BY_ID, 'CREATED_BY', {
      inputType: 'multiSelect',
      options: agentOptions.value,
      dataType: 'number',
      filterOperators: presenceOperators.value,
    }),
    attribute(KANBAN_TASK_ATTRIBUTES.DUE_DATE, 'DUE_DATE', {
      inputType: 'date',
      filterOperators: [...dateOperators.value, ...presenceOnly()],
    }),
    attribute(KANBAN_TASK_ATTRIBUTES.START_DATE, 'START_DATE', {
      inputType: 'date',
      filterOperators: [...dateOperators.value, ...presenceOnly()],
    }),
    attribute(KANBAN_TASK_ATTRIBUTES.CREATED_AT, 'CREATED_AT', {
      inputType: 'date',
      filterOperators: dateOperators.value,
    }),
    attribute(KANBAN_TASK_ATTRIBUTES.UPDATED_AT, 'UPDATED_AT', {
      inputType: 'date',
      filterOperators: dateOperators.value,
    }),
    attribute(KANBAN_TASK_ATTRIBUTES.STEP_CHANGED_AT, 'STEP_CHANGED_AT', {
      inputType: 'date',
      filterOperators: dateOperators.value,
    }),
    ...customFilterTypes.value,
  ]);

  return { filterTypes };
}
